import moment from "moment";
import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Clock, Play, Square } from "lucide-react";
import MainLayout from "@/components/layouts";
import { TaskChecklist } from "./components/TaskChecklist";
import { getDailyWorkDayWithClockRecords, goOffSite, goOnSite, tickClockRecordTask, writeClockRecordNotes } from "./daily-work.api";
import { DailyWorkDayJoin, type DayWithClockRecords } from "@/repositories/daily-work.repository";
import { DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { isWorkedToday, workDayDateAt } from "@shyne/shared";
import {
  OFF_SITE_ACTION,
  OFF_SITE_LABEL,
  ON_SITE_ACTION,
  ON_SITE_LABEL,
  formatSiteStatus,
  getOffSiteLockMessage,
} from "@/lib/utils/daily-work-site-gate";

function isExecutionForToday(execution: DailyWorkExecutionJoin | null): boolean {
  return execution !== null && isWorkedToday(workDayDateAt(new Date(execution.created_at)));
}

export default function DailyWorkExecutionDetail() {
  const { dayId, executionId } = useParams<{ dayId: string; executionId?: string }>();
  const [searchParams] = useSearchParams();
  const isReadOnlyMode = searchParams.get("readonly") === "true";
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState<DailyWorkDayJoin | null>(null);
  const [execution, setExecution] = useState<DailyWorkExecutionJoin | null>(null);
  const [executionHistory, setExecutionHistory] = useState<DailyWorkExecutionJoin[]>([]);
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!dayId) return;

      try {
        setLoading(true);
        const { day: dayData, clockRecords: historyData } = await getDailyWorkDayWithClockRecords(dayId);

        setDay(dayData);
        setExecutionHistory(historyData);

        if (executionId) {
          const executionData = historyData.find((record) => record.id === executionId);
          if (!executionData) throw new Error("Execution not found");
          setExecution(executionData);
          setNotes(executionData.notes || "");
        } else {
          const todayExecution = historyData.find(isExecutionForToday);

          if (todayExecution) {
            setExecution(todayExecution);
            setNotes(todayExecution.notes || "");
          } else {
            // No execution exists yet - will be created when the worker goes on-site
            setExecution(null);
            setNotes("");
          }
        }
      } catch (error: any) {
        toast.error(error.message || "Failed to load execution details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dayId, executionId]);

  const showDay = ({ day: shown, clockRecords }: DayWithClockRecords) => {
    setDay(shown);
    setExecutionHistory(clockRecords);
    setExecution(clockRecords[0] ?? null);
  };

  const handleClockIn = async () => {
    if (!day) return;

    try {
      setUpdating(true);
      showDay(await goOnSite(day.id));
      toast.success("You're on-site");
    } catch (error: any) {
      toast.error(error.message || "Failed to go on-site");
    } finally {
      setUpdating(false);
    }
  };

  const handleClockOut = async () => {
    if (!execution || !day) return;
    const lockMessage = getOffSiteLockMessage({ tasks: execution.tasks, notes });
    if (lockMessage) {
      toast.error(lockMessage);
      return;
    }

    try {
      setUpdating(true);
      showDay(await goOffSite(day.id, notes));
      toast.success("You're off-site");
    } catch (error: any) {
      toast.error(error.message || "Failed to go off-site");
    } finally {
      setUpdating(false);
    }
  };

  const handleTaskToggle = async (position: number, done: boolean) => {
    if (!execution || !day) return;

    try {
      setUpdating(true);
      showDay(await tickClockRecordTask(day.id, position, done));
    } catch (error: any) {
      toast.error(error.message || "Failed to update task");
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!execution || !day) return;

    try {
      setUpdating(true);
      showDay(await writeClockRecordNotes(day.id, notes));
      toast.success("Notes saved successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to save notes");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <MainLayout breadcrumb={[{ name: "Daily Work", href: "/daily-work" }]}>
        <div className="flex items-center justify-center h-64">
          <p>Loading execution details...</p>
        </div>
      </MainLayout>
    );
  }

  if (!day) {
    return (
      <MainLayout breadcrumb={[{ name: "Daily Work", href: "/daily-work" }]}>
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">Daily work day not found</p>
        </div>
      </MainLayout>
    );
  }

  // Create a default execution structure if it doesn't exist (for displaying tasks/notes)
  const defaultExecution: DailyWorkExecutionJoin | null = !execution
    ? {
        id: "",
        clock_in: null,
        clock_out: null,
        tasks: day.tasks.map((t) => ({ name: t, done: false })),
        notes: "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        daily_work_day: {
          id: day.id,
          work_date: day.work_date,
          tasks: day.tasks,
          service: {
            id: day.service.id,
            name: day.service.name,
            parent_name: day.service.parent_name,
          },
          assigned_user: day.assigned_user
            ? {
                id: day.assigned_user.id,
                name: day.assigned_user.name,
              }
            : null,
          assigned_org: day.assigned_org
            ? {
                id: day.assigned_org.id,
                display_name: day.assigned_org.display_name,
              }
            : null,
        },
      }
    : null;

  const displayExecution = execution || defaultExecution;
  const displayNotes = execution ? notes : "";


  // At this point, displayExecution must exist (we've handled null cases above)
  if (!displayExecution) {
    return null; // This should never happen, but TypeScript guard
  }

  const breadcrumb = [
    { name: "Daily Work", href: "/daily-work", current: false },
    {
      name: `${day.daily_work_data.property.name} - ${format(parseISO(day.work_date), "MMM d, yyyy")}`,
      href: "#",
      current: true,
    },
  ];

  const isTodayDate = isWorkedToday(day.work_date);
  const isTodayExecution = execution ? isExecutionForToday(execution) : false;
  
  // Read-only if:
  // 1. Read-only mode from query param
  // 2. Not today's date
  // 3. Viewing historical execution (executionId exists)
  // 4. Clocked out (execution exists and has clock_out)
  const isReadOnly = 
    isReadOnlyMode || 
    !isTodayDate || 
    !!executionId || 
    (execution?.clock_out !== null && execution?.clock_out !== undefined);

  // Can only interact if execution exists, clocked in, but not clocked out
  const canEditTasksAndNotes = execution && execution.clock_in && !execution.clock_out && !isReadOnly;
  const canClockIn = !displayExecution.clock_in && !isReadOnly && isTodayDate;
  const canClockOut = displayExecution.clock_in && !displayExecution.clock_out && !isReadOnly;
  const offSiteLockMessage = canClockOut
    ? getOffSiteLockMessage({ tasks: displayExecution.tasks, notes })
    : null;

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="container mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link to="/daily-work">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Daily Work
            </Button>
          </Link>
        </div>

        {/* Overview Card */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Work Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Property</p>
              <p className="font-medium">{day.daily_work_data.property.name}</p>
              {day.daily_work_data.property.address && (
                <p className="text-sm text-muted-foreground">{day.daily_work_data.property.address}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Date</p>
              <p className="font-medium">{format(parseISO(day.work_date), "MMM d, yyyy")}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Service</p>
              <p className="font-medium">{day.service.name}</p>
              {day.service.parent_name && <p className="text-sm text-muted-foreground">{day.service.parent_name}</p>}
              {day.duration_label && <p className="text-sm text-muted-foreground">{day.duration_label}</p>}
            </div>
            {day.instructions && (
              <div className="md:col-span-3">
                <p className="text-sm text-muted-foreground">Instructions</p>
                <p className="text-sm whitespace-pre-wrap">{day.instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* On-site / Off-site Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Tracking
            </CardTitle>
            {isReadOnly && (
              <p className="text-sm text-muted-foreground mt-2">
                {!isTodayDate
                  ? `This work is scheduled for ${format(parseISO(day.work_date), "MMM d, yyyy")}. Today is ${format(parseISO(workDayDateAt(new Date())), "MMM d, yyyy")}.`
                  : "Viewing historical execution. Editing is disabled."}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">{ON_SITE_LABEL}</p>
                <div className="flex items-center gap-2">
                  <Badge variant={displayExecution.clock_in ? "default" : "secondary"} className="text-lg px-4 py-2">
                    {formatSiteStatus("on", displayExecution.clock_in)}
                  </Badge>
                  {canClockIn && (
                    <Button onClick={handleClockIn} disabled={updating} size="sm" data-testid="daily-work-on-site">
                      <Play className="w-4 h-4 mr-2" />
                      {ON_SITE_ACTION}
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">{OFF_SITE_LABEL}</p>
                <div className="flex items-center gap-2">
                  <Badge variant={displayExecution.clock_out ? "default" : "secondary"} className="text-lg px-4 py-2">
                    {formatSiteStatus("off", displayExecution.clock_out)}
                  </Badge>
                  {canClockOut && (
                    <Button
                      onClick={handleClockOut}
                      disabled={updating || !!offSiteLockMessage}
                      size="sm"
                      variant="destructive"
                      data-testid="daily-work-off-site"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      {OFF_SITE_ACTION}
                    </Button>
                  )}
                </div>
                {offSiteLockMessage && (
                  <p className="text-xs text-muted-foreground" data-testid="daily-work-off-site-lock">
                    {offSiteLockMessage}
                  </p>
                )}
              </div>
            </div>
            {displayExecution.clock_in && displayExecution.clock_out && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">Total Time</p>
                <p className="text-lg font-medium">
                  {calculateTimeDifference(displayExecution.clock_in, displayExecution.clock_out)}
                </p>
              </div>
            )}
            {!execution && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground text-center">
                  Mark yourself on-site to begin tracking and enable task completion
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Task Checklist */}
        {/* Disabled while a save is in flight: toggles are built from the last saved
            execution, so a second toggle before the first returns would overwrite it. */}
        <TaskChecklist
          tasks={displayExecution.tasks}
          onTaskToggle={handleTaskToggle}
          readOnly={!canEditTasksAndNotes || updating}
        />

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={displayNotes}
              onChange={(e) => {
                if (execution) {
                  setNotes(e.target.value);
                }
              }}
              placeholder="Add any notes about this execution..."
              rows={4}
              disabled={!canEditTasksAndNotes}
            />
            <Button onClick={handleSaveNotes} disabled={updating || !canEditTasksAndNotes}>
              Save Notes
            </Button>
            {!execution && (
              <p className="text-xs text-muted-foreground text-center">
                Mark yourself on-site to enable notes editing
              </p>
            )}
          </CardContent>
        </Card>

        {/* Execution History */}
        {executionHistory.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {executionHistory.map((hist) => (
                  <div
                    key={hist.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">{moment(hist.created_at).format("MM/DD/YYYY")}</p>
                      <p className="text-sm text-muted-foreground">
                        {hist.clock_in && hist.clock_out
                          ? `${hist.clock_in} - ${hist.clock_out}`
                          : formatSiteStatus("on", hist.clock_in)}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {hist.tasks.filter((t) => t.done).length} / {hist.tasks.length} tasks
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}

function calculateTimeDifference(clockIn: string, clockOut: string): string {
  const [inHours, inMinutes] = clockIn.split(":").map(Number);
  const [outHours, outMinutes] = clockOut.split(":").map(Number);

  const inTotalMinutes = inHours * 60 + inMinutes;
  const outTotalMinutes = outHours * 60 + outMinutes;

  const diffMinutes = outTotalMinutes - inTotalMinutes;
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return `${hours}h ${minutes}m`;
}

